import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useGetJobs } from '~/hooks/apis/aws-jobs/useGetJobs';

import { restoreFailedJobs } from '~/services/AwsJobsService';

import JobsTable from './JobsTable';
import { IActions, ITabs } from './types';

interface Props {
  selectedTab: ITabs;
}

interface Modal {
  name: string;
  title: string;
  message: string;
  open: boolean;
  isLoading: boolean;
  action: IActions;
}

const defaultModal: Modal = {
  name: '',
  title: '',
  message: '',
  open: false,
  isLoading: false,
  action: 'restore',
};

function AwsJobsPageContainer({ selectedTab }: Props) {
  const [modal, setModal] = useState<Modal>(defaultModal);
  const { enqueueSnackbar } = useSnackbar();

  const { data: jobs, isFetching, error, refetch } = useGetJobs(selectedTab);

  if (error) {
    enqueueSnackbar(error instanceof Error ? error.message : 'Failed to fetch jobs', { variant: 'error' });
  }

  const closeModal = useCallback(() => {
    setModal(defaultModal);
  }, []);

  const openModal = useCallback(
    (name: string, action: IActions) => {
      if (jobs) {
        const job = jobs.find((x) => x.name === name);

        if (action === 'restore' && job) {
          setModal({
            name,
            title: 'Are you sure?',
            message: `Are you sure that you want to restore ${job.info.failed} failed jobs the ${name}?`,
            open: true,
            isLoading: false,
            action,
          });
        }
      }
    },
    [jobs],
  );

  const submitModal = useCallback(() => {
    setModal((prev) => ({ ...prev, isLoading: true }));

    const fn = {
      restore: async () => restoreFailedJobs(selectedTab, modal.name),
    };

    if (fn[modal.action]) {
      fn[modal.action]()
        .then(() => {
          refetch();
          setModal((prev) => ({ ...prev, open: false, isLoading: false }));
        })
        .catch((e) => {
          setModal((prev) => ({ ...prev, isLoading: false }));
          enqueueSnackbar(e instanceof Error ? e.message : `Failed to ${modal.action} jobs`, { variant: 'error' });
        });
    }
  }, [jobs, modal, selectedTab, refetch, enqueueSnackbar]);

  return (
    <Stack direction="column" spacing={2}>
      <Button
        variant="outlined"
        onClick={() => refetch()}
        endIcon={isFetching ? <Spinner size={15} inline /> : <RefreshIcon />}
        color="neutral"
        disabled={isFetching}
      >
        Refresh
      </Button>
      <JobsTable data={jobs ?? []} openModal={openModal} />
      <Dialog open={modal.open} onClose={closeModal}>
        <DialogTitle>
          <Typography fontWeight="bold" fontSize="large">
            {modal.title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: 4, minWidth: '400px' }}>
          <Stack direction="column" spacing={1}>
            <Box>
              <Typography fontWeight="bold">{modal.message}</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          {modal.isLoading && <Spinner size={25} />}
          <Button onClick={closeModal} disabled={modal.isLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitModal} disabled={modal.isLoading}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default AwsJobsPageContainer;
